import { useCallback, useEffect, useState } from "react";
import { ConfigStorage, DashboardGroup, GroupShow, dashboardConfigStore, newGroupId } from "../../services/dashboardConfig";

/**
 * Groups of areas for one dashboard, with their totals shown on the maps.
 * Loads when the project changes; persists on user actions only (a mount-time
 * write would race the restore under StrictMode). Idle when there's no project.
 * `storage` says where edits go — the server, or this device where the server
 * has no config route yet.
 */
export function useDashboardConfig(projectId: string | null) {
  const [groups, setGroups] = useState<DashboardGroup[]>([]);
  const [storage, setStorage] = useState<ConfigStorage>("device");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId) {
      setGroups([]);
      setStorage("device");
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    dashboardConfigStore
      .load(projectId)
      .then((r) => {
        if (cancelled) return;
        setGroups(r.config.groups);
        setStorage(r.storage);
        setError(r.error ?? null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const commit = useCallback(
    (next: DashboardGroup[]) => {
      setGroups(next); // optimistic — the map updates at once
      if (!projectId) return;
      dashboardConfigStore
        .save(projectId, { groups: next }, storage)
        .then(() => setError(null))
        .catch((err) => setError(err instanceof Error ? err.message : "Could not save totals"));
    },
    [projectId, storage]
  );

  const addGroup = useCallback(
    (name = "New total"): DashboardGroup => {
      const g = { id: newGroupId(), name, areaIds: [] };
      commit([...groups, g]);
      return g;
    },
    [groups, commit]
  );
  const renameGroup = useCallback(
    (id: string, name: string) => commit(groups.map((g) => (g.id === id ? { ...g, name } : g))),
    [groups, commit]
  );
  const removeGroup = useCallback((id: string) => commit(groups.filter((g) => g.id !== id)), [groups, commit]);
  const setGroupShow = useCallback(
    (id: string, show: GroupShow) => commit(groups.map((g) => (g.id === id ? { ...g, show } : g))),
    [groups, commit]
  );
  const toggleArea = useCallback(
    (groupId: string, areaId: string) =>
      commit(
        groups.map((g) =>
          g.id !== groupId
            ? g
            : { ...g, areaIds: g.areaIds.includes(areaId) ? g.areaIds.filter((a) => a !== areaId) : [...g.areaIds, areaId] }
        )
      ),
    [groups, commit]
  );

  return { groups, storage, loading, error, enabled: !!projectId, addGroup, renameGroup, removeGroup, setGroupShow, toggleArea };
}

/** What the map overlays need to drive group editing; the page owns the state. */
export interface GroupEditing {
  group: DashboardGroup | null; // the group being edited, if any
  storage: ConfigStorage; // where edits are saved
  error?: string | null; // last save/load problem, if any
  onEdit: (groupId: string) => void;
  onAdd: () => void;
  onRename: (name: string) => void;
  onDelete: () => void;
  onSetShow: (show: GroupShow) => void;
  onDone: () => void;
  onToggleArea: (areaId: string) => void;
}
